import React, { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase-config";
import { getRequest, postRequest } from "../apiWrapper";
import getBrandFromHostname from "../utils/getBrandFromHostname";
import { HOSTS } from "../constants/hosts";

const TeacherLoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isWaitingApproval, setIsWaitingApproval] = useState(false);

  const queryParams = new URLSearchParams(window.location.search);
  const redirectTo = queryParams.get("redirectTo") || "report";
  const redirectQuery = redirectTo ? `?redirectTo=${redirectTo}` : "";

  // テーマ設定
  const themeConfig = {
    report: {
      bgColor: "bg-gray-100",
      primaryColor: "text-gray-800",
      buttonBg: "bg-blue-500 hover:bg-blue-600",
      linkColor: "text-blue-500 hover:text-blue-600",
      headerText: "レポート講師ログイン",
    },
  schedule: {
    bgColor: "bg-green-50",                         // 薄緑背景
    primaryColor: "text-green-800",                 // 見出し文字を濃い緑に
    buttonBg: "bg-green-500 hover:bg-green-600",    // ボタン背景を緑系に
    linkColor: "text-green-600 hover:text-green-800", // リンクも緑系に
    headerText: "スケジュール講師ログイン",
  },
  };
  const theme = themeConfig[redirectTo] || themeConfig.report;

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setLoading(true);
    console.log("handleLogin: 開始");

    try {
      // Firebase Authentication で認証
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const tokenResult = await user.getIdTokenResult(true);
      let jwtToken = await user.getIdToken(); // JWT トークンを取得
      sessionStorage.setItem("jwtToken", jwtToken);

      const role = tokenResult.claims.role;

      if (!role) {
        throw new Error("ロール情報が取得できません");
      }

      if (role === "admin") {
        throw new Error("管理者アカウントは別のログインページを使用してください");
      }

      if (role !== "teacher") {
        throw new Error("許可されていないログインです");
      }

      let teacherData = null;

      if (redirectTo === "report") {
        // レポートでは承認が必要
        teacherData = await getRequest("teacherApprovalStatus");

        while (!teacherData.approved) {
          setErrorMessage("管理者の承認待ちです。承認されると自動的にログインします。");
          setIsWaitingApproval(true);
          await new Promise(resolve => setTimeout(resolve, 10000));
          teacherData = await getRequest("teacherApprovalStatus");
        }
      } else {
        // スケジュールでは承認不要、APIから取得
        const teacherRes = await getRequest("teachers");
        teacherData = teacherRes.teachers?.[0];
        if (!teacherData) throw new Error("講師情報の取得に失敗しました");
      }

      // カスタムトークン取得
      const uid = user.uid;
      const response = await postRequest("generate-token", { uid });

      const data = await response.json();
      if (!data.customToken) {
        throw new Error("カスタムトークン取得失敗:", data.error);
      }

      onLogin(teacherData.id, teacherData.name);

      // HOSTSキーの組み立て
      const redirectKey = `teacher${redirectTo.charAt(0).toUpperCase()}${redirectTo.slice(1)}`;

      // HOSTSに存在しないキーだったらデフォルトに戻す
      const targetHost = HOSTS[redirectKey] || HOSTS.teacherReport;

      window.location.href = `https://${targetHost}.${getBrandFromHostname()}.classora.jp?token=${data.customToken}`;

    } catch (error) {
      console.error("handleLogin: ログインエラー", error);
      setErrorMessage("ログインに失敗しました。メールアドレスまたはパスワードを確認してください。");
    } finally {
      setLoading(false);
    }
  };

  // 上部の切り替えUI
  const AppSwitcher = () => {
    return (
      <div className="flex mb-6">
        <a
          href="?redirectTo=report"
          className={`px-4 py-2 border rounded-l ${redirectTo === "report"
            ? "bg-white font-bold"
            : "bg-gray-200 hover:bg-gray-300"
            }`}
        >
          レポート
        </a>
        <a
          href="?redirectTo=schedule"
          className={`px-4 py-2 border-t border-b border-r rounded-r ${redirectTo === "schedule"
            ? "bg-white font-bold"
            : "bg-gray-200 hover:bg-gray-300"
            }`}
        >
          スケジュール
        </a>
      </div>
    );
  };

  return (
    <div className={`flex flex-col items-center justify-center h-screen ${theme.bgColor}`}>
      <h1 className={`text-3xl font-bold mb-6 ${theme.primaryColor}`}>{theme.headerText}</h1>
      <AppSwitcher />
      <form className="bg-white p-6 rounded shadow-md w-96" onSubmit={handleLogin}>
        <div className="mb-4">
          <label className="block text-sm font-bold mb-2">メールアドレス</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border rounded"
            placeholder="メールアドレスを入力"
            required
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-bold mb-2">パスワード</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border rounded"
            placeholder="パスワードを入力"
            required
          />
        </div>
        {errorMessage && <p className="text-red-500 text-sm mb-4">{errorMessage}</p>}
        <button
          type="submit"
          className={`w-full ${theme.buttonBg} text-white py-2 rounded transition`}
          disabled={loading || isWaitingApproval}
        >
          {loading ? "ログイン中..." : isWaitingApproval ? "承認待ち" : "ログイン"}
        </button>
        <div className="mt-4 text-center">
          <a
            href={`https://${HOSTS.adminLogin}.${getBrandFromHostname()}.classora.jp${redirectQuery}`}
            className={`transition ${theme.linkColor}`}
          >
            管理者用のページはこちら
          </a>
        </div>
      </form>
    </div>
  );
};

export default TeacherLoginPage;
